"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
// import { setCookie, deleteCookie, delCookies } from "../utils/cookies";
import { useState } from "react";
import { useLocalStorage } from "./use-local-storage";
import { req } from "../api";
import { toast } from "sonner";
import handleAxiosError from "../error";
import { PENDING_KYC_STATUSES, ROUTES } from "../constants";
import { platform } from "../platform";
import { track, identify } from "../analytics";

const HOME_PAGE = "/home";
// const SIGNIN_PAGE = "/signin";
const VERIFY_PAGE = "/signup/verify";

type User = {
  id: string;
  email: string;
  firstName: string;
  displayName: string;
  avatarUrl?: string;
  role?: string;
  kycVerified: boolean;
};

export type KycStatus =
  | "created"
  | "in-review"
  | "submitted"
  | "verified"
  | "rejected";

export type Profile = {
  id: string;
  userId: string;
  bio?: string;
  email: string;
  firstName: string;
  displayName: string;
  tagName?: string;
  avatarUrl?: string;
  isKycVerified?: boolean;
  kycStatus: KycStatus;
  location?: string;
  emailVerified: boolean;
};

type SignInResponse = {
  user?: User;
  access?: {
    token: string;
    expires: string;
  };
  isKycVerified?: boolean;
  refresh?: {
    token: string;
    expires: string;
  } | null;
  profile?: Profile;
  // MFA related
  mfaRequired?: boolean;
  mfaToken?: string;
  expiresIn?: number;
};

type SignUpResponse = { user: User };

type SignInData = {
  email: string;
  password?: string;
  platform?: string;
};

type SignUpData = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  platform?: string;
  countryCode?: string;
  organisationType?: "individual" | "company";
  organisationName?: string;
};

type VerifyResponse = {
  access: {
    token: string;
    expires: string;
  };
  user: User;
  profile: Profile;
};

// Query keys for cache management
export const authKeys = {
  all: ["auth"] as const,
  user: () => [...authKeys.all, "user"] as const,
  emailStatus: () => [...authKeys.all, "email-status"] as const,
};

// ============================================
// Main hook: Get current user session
// ============================================
export function useProfile() {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: async () => {
      const user = await req<Profile>("/auth/profile");
      return user;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry auth failures
    refetchOnWindowFocus: true, // Revalidate when user returns
  });
}

// Derived helper hook for authentication status
export function useAuth() {
  const { data: user, isLoading, isError, error } = useProfile();

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    isError,
    error,
  };
}

// ============================================
// Sign in mutation
// ============================================
export function useSignIn({ platform = "buyer" } = {}) {
  const { mutate: resendOTP } = useResendOTP();
  const router = useRouter();
  const handleAuthSuccess = useAuthSuccess();

  return useMutation({
    mutationFn: async (credentials: SignInData) => {
      const response = await req.post<SignInResponse, SignInData>(
        `/auth/signin`,
        {
          ...credentials,
          platform,
        },
      );
      return response;
    },
    onSuccess: (data) => {
      if (data.user) {
        identify(data.user.email, {
          FIRSTNAME: data.user.firstName,
        });
        track("login", { method: "email" });
      }
      handleAuthSuccess(data);
    },
    onError: (error, variables) => {
      if (error.message === "User email verification failed") {
        router.push(VERIFY_PAGE);
        resendOTP(variables.email);
      }
      if (error.message.startsWith("Email verification required")) {
        router.push(VERIFY_PAGE);
        resendOTP(variables.email);
      }
      // toast.error(error.message);
    },
  });
}

// ============================================
// MFA login verification mutation
// ============================================
export function useMFAVerify() {
  const handleAuthSuccess = useAuthSuccess();

  return useMutation({
    mutationFn: async (data: { mfaToken: string; code: string }) => {
      const response = await req.post<
        SignInResponse,
        { mfaToken: string; code: string }
      >(`/auth/mfa/verify`, data);
      return response;
    },
    onSuccess: handleAuthSuccess,
    onError: (error) => {
      if (error.message === "MFA_TOKEN_EXPIRED") {
        toast.error("Session expired. Please login again.");
        window.location.reload();
      }
    },
  });
}

export function useOAuthCallback() {
  const handleAuthSuccess = useAuthSuccess();

  return useMutation({
    mutationFn: async (data: { code: string; platform: string }) => {
      const response = await req.post<
        SignInResponse,
        { code: string; platform: string }
      >(`/auth/google/callback`, data);
      return response;
    },
    onSuccess: handleAuthSuccess,
  });
}

export type OAuthInitData = {
  platform: string;
  callbackUrl: string;
};

export type OAuthInitResponse = {
  attemptId: string;
  authUrl: string;
};

export type OAuthCompleteResponse = SignInResponse & {
  isNewUser: boolean;
  requiresPhoneNumber?: boolean;
  attemptId?: string;
  message?: string;
};

export type OAuthPhoneSubmission = {
  attemptId: string;
  phoneNumber: string;
};

export function useOAuthInit() {
  return useMutation({
    mutationFn: async (data: OAuthInitData) => {
      return await req.post<OAuthInitResponse, OAuthInitData>(
        "/auth/google/init",
        data,
      );
    },
    onSuccess: (data) => {
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    },
  });
}

export function useOAuthComplete() {
  const handleAuthSuccess = useAuthSuccess();
  const router = useRouter();
  return useMutation({
    mutationFn: async (attemptId: string) => {
      return await req<OAuthCompleteResponse>(
        `/auth/google/complete/${attemptId}`,
      );
    },
    onSuccess: (data) => {
      // Don't complete auth if phone number is required
      if (data.requiresPhoneNumber) {
        return;
      }

      if (data.user) {
        identify(data.user.email, {
          FIRSTNAME: data.user.firstName,
        });
        if (data.isNewUser) {
          // track("registration_started", { method: "google" });
          track("registration_completed", { method: "google" });
        } else {
          track("login", { method: "google" });
        }
      }
      handleAuthSuccess(data);
    },
    onError: () => {
      // console.log(error);
      router.replace("/signin");
    },
  });
}

export function useSubmitOAuthPhone() {
  const handleAuthSuccess = useAuthSuccess();

  return useMutation({
    mutationFn: async (data: OAuthPhoneSubmission) => {
      return await req.post<SignInResponse, { phoneNumber: string }>(
        `/auth/google/complete/${data.attemptId}/phone`,
        { phoneNumber: data.phoneNumber },
      );
    },
    onSuccess: (data) => {
      track("oauth_phone_completed");
      handleAuthSuccess(data);
    },
  });
}

export function useLookup(platform = "buyer") {
  const router = useRouter();
  const [isRegistered, setIsRegistered] = useState(false);
  const { setEmail } = useSignupEmail();

  const mutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await req<{ isRegistered: boolean; hasAccess: boolean }>(
        `/auth/lookup/${encodeURIComponent(email)}?platform=${platform}`,
        "get",
      );
      return response;
    },
    onSuccess: (data, email) => {
      if (!data.isRegistered) {
        setEmail(email);
        router.push("/signup");
      } else {
        setIsRegistered(true);
      }
    },
  });

  return {
    lookupUser: mutation.mutate,
    isRegistered,
    loading: mutation.isPending,
    resetLookup: () => setIsRegistered(false),
  };
}

// ============================================
// Sign up mutation
// ============================================
export function useSignUp({ platform = "buyer" }) {
  const router = useRouter();
  const { setEmail } = useSignupEmail();

  return useMutation({
    mutationFn: async (userData: SignUpData) => {
      const response = await req.post<SignUpResponse, SignUpData>(
        `/auth/signup`,
        { ...userData, platform },
      );
      return response;
    },
    onSuccess: (data, variables) => {
      setEmail(data.user.email);
      identify(data.user.email, {
        FIRSTNAME: variables.firstName,
        LASTNAME: variables.lastName,
      });
      track("registration_started", { platform });
      router.push(VERIFY_PAGE);
    },
  });
}

// ============================================
// Sign out mutation
// ============================================
export function useSignOut() {
  const queryClient = useQueryClient();
  const router = useRouter();

  const mutation = useMutation({
    mutationFn: async () => {
      toast.info("Signing out...", { id: "signout" });
      return await req.post("/auth/signout", undefined, "Logout failed");
    },
    onSuccess: () => {
      // Clear all cached data
      router.push("/");
      queryClient.clear();
      queryClient.cancelQueries();
    },
    onError: (error) => {
      const errorRes = handleAxiosError(error);
      toast.error(errorRes.message, { id: "signout" });
    },
  });

  return {
    signOut: () => mutation.mutate(),
    isPending: mutation.isPending,
  };
}

// ============================================
// Email verification mutations
// ============================================
export function useResendOTP() {
  return useMutation({
    mutationFn: async (email: string) => {
      return await req.post<{ message: string }, { email: string }>(
        "/auth/email/verify",
        { email },
        "Failed to send verification email",
      );
    },
  });
}

export function useVerifyEmail() {
  const { clearEmail } = useSignupEmail();
  const handleAuthSuccess = useAuthSuccess();

  return useMutation({
    mutationFn: async ({ otp, email }: { otp: string; email: string }) => {
      const res = await req.post<VerifyResponse, { email: string }>(
        `/auth/email/verify/${otp}`,
        { email },
      );
      return res;
    },
    onSuccess: (data) => {
      clearEmail();
      sessionStorage.removeItem("reg_pending");
      track("registration_completed");
      handleAuthSuccess(data);
    },
  });
}
export function useEmailStatus() {
  return useQuery({
    queryKey: authKeys.emailStatus(),
    queryFn: async () => {
      return await req<{ verified: boolean }>("/auth/email/status", "get");
    },
    enabled: false, // Only fetch when explicitly called with refetch()
  });
}

// ============================================
// Password recovery mutations
// ============================================
export function useForgotPassword() {
  return useMutation({
    mutationFn: async (email: string) => {
      return await req.post<{ message: string }, { email: string }>(
        "/auth/forgot-password",
        { email },
        "Failed to send recovery email",
      );
    },
  });
}

type ResetPasswordReq = {
  token?: string;
  email: string;
  newPassword?: string;
};

export function useResetPassword() {
  const router = useRouter();

  return useMutation({
    mutationFn: async ({ token, ...data }: ResetPasswordReq) => {
      return await req.post<{ message: string }, ResetPasswordReq>(
        `/auth/forgot-password/${token}`,
        data,
        "Failed to reset password",
      );
    },
    onSuccess: () => {
      toast.success("Password reset successful!");
      router.push("/signin");
    },
  });
}

type ChangePasswordReq = {
  currentPassword: string;
  newPassword: string;
};

export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: ChangePasswordReq) => {
      return await req.put<{ message: string }, ChangePasswordReq>(
        "/auth/password",
        data,
        "Failed to change password",
      );
    },
    onSuccess: () => {
      toast.success("Password changed successfully!");
    },
  });
}

// ============================================
// Update user image
// ============================================
export function useUpdateUserImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (imageUrl: string) => {
      return await req.put<User, { image: string }>(
        "/auth/user/image",
        { image: imageUrl },
        "Failed to update image",
      );
    },
    onMutate: async (newImageUrl) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: authKeys.user() });

      // Snapshot previous value
      const previousUser = queryClient.getQueryData<User>(authKeys.user());

      // Optimistically update to new value
      if (previousUser) {
        queryClient.setQueryData<User>(authKeys.user(), {
          ...previousUser,
          avatarUrl: newImageUrl,
        });
      }

      return { previousUser };
    },
    onError: (err, newImageUrl, context) => {
      // Rollback on error
      if (context?.previousUser) {
        queryClient.setQueryData(authKeys.user(), context.previousUser);
      }
    },
    onSettled: () => {
      // Refetch to ensure we have correct data
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
}

export function useSignupEmail() {
  const [email, setEmail, clearEmail] = useLocalStorage<string>(
    "signup_email",
    "",
  );

  return {
    email,
    setEmail,
    clearEmail,
  };
}

type AuthSuccessData = {
  access?: { token: string };
  profile?: Profile;
  mfaRequired?: boolean;
};

export function useAuthSuccess() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [authRedirect, setAuthRedirect] = useLocalStorage(
    "authRedirect",
    HOME_PAGE,
  );

  return (data: AuthSuccessData) => {
    if (data.mfaRequired) return;

    if (data.profile) {
      queryClient.setQueryData(authKeys.user(), data.profile);
      if (
        PENDING_KYC_STATUSES.includes(data.profile.kycStatus) &&
        !platform.isAdmin &&
        !platform.isBuyer
      ) {
        router.replace(ROUTES.VERIFY);
        return;
      }
    }

    if (data.access?.token) {
      router.replace(authRedirect);
      setAuthRedirect(HOME_PAGE);
    }
  };
}
